import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    TablePagination,
    MenuItem,
    Menu,
    CircularProgress,
    Card,
    Tooltip,
    FormControl,
    Select,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useLazyQuery, gql } from '@apollo/client';
import * as XLSX from 'xlsx';
import { useAuth } from '../../hooks/useAuth';
import { useApolloClient } from '@apollo/client';


const GET_KYC_PROCESSING_DATA = gql`
query GetKycProcessingData($offset: Int, $limit: Int, $status: String, $searchTerm: String, $searchType: String) {
  getKycProcessingData(offset: $offset, limit: $limit, status: $status, searchTerm: $searchTerm, searchType: $searchType) {
    success
    data {
      kyc_id
      vin
      imei
      engine_number
      year
      vehicle_make
      vehicle_model
      fuel_type
      customer_name
      customer_mobile
      retail_invoice_number
      customer_address
      customer_email
      dealer_code
      dealer_name
      dealer_address
      dealer_town
      dealer_contact_name
      kyc_count
      kyc_approved_date
      kyc_rejected_date
      last_kyc_recieved_date
      kyc_mode
      proof_of_address
      proof_of_identity
      ticket_created
      status
      rejection_reason
      request_id
      project_name
      device_model
      device_serial_number
      device_imei_number
      iccid_number
      customer_number
      customer_otp_number
      registration_state
      rto_name
      rto_number
      dealer_contact_number
      alternate_dealer_name
      alternate_dealer_number
      sim_activation_status
      msisdn_1
      msisdn_2
      vehicle_type
      year_of_manufacture
      comman_layer_cert
      common_layer_cert_issued_date
      fitment_cert
      fitment_cert_issued_date
    }
    count
    error
  }
}
`;

const cellStyle = {
    fontSize: '14px',
    padding: '8px 12px',
    borderBottom: '1px solid #e0e0e0',
    borderRight: '1px solid #e0e0e0',
    color: '#000',
    whiteSpace: 'nowrap',
    maxWidth: '800px'
};

const columnStyles = {
    srNo: {
        ...cellStyle,
        maxWidth: '80px'
    },
    action: {
        ...cellStyle,
        maxWidth: '140px',
    },
    dealer_name: {
        ...cellStyle,
        maxWidth: '800px',
    },

};

interface KycProcessingData {
    kyc_id: string;
    vin: string;
    imei: string;
    engine_number: string;
    year: string;
    vehicle_make: string;
    vehicle_model: string;
    fuel_type: string;
    customer_name: string;
    customer_mobile: string;
    retail_invoice_number: string;
    customer_address: string;
    customer_email: string;
    dealer_code: string;
    dealer_name: string;
    dealer_address: string;
    dealer_town: string;
    dealer_contact_name: string;
    kyc_count: string;
    kyc_approved_date: string;
    kyc_rejected_date: string;
    last_kyc_recieved_date: string;
    kyc_mode: string;
    proof_of_address: string;
    proof_of_identity: string;
    ticket_created: string;
    status: string;
    rejection_reason: string;
    request_id: string;
    project_name: string;
    device_model: string;
    device_serial_number: string;
    device_imei_number: string;
    iccid_number: string;
    customer_number: string;
    customer_otp_number: string;
    registration_state: string;
    rto_name: string;
    rto_number: string;
    dealer_contact_number: string;
    alternate_dealer_name: string;
    alternate_dealer_number: string;
    sim_activation_status: string;
    msisdn_1: string;
    msisdn_2: string;
    vehicle_type: string;
    year_of_manufacture: string;
    comman_layer_cert: string;
    common_layer_cert_issued_date: string;
    fitment_cert: string;
    fitment_cert_issued_date: string;
}

const KYC_STATUS_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'KYC_RECEIVED', value: 'KYC_RECEIVED' },
    { label: 'KYC_APPROVED', value: 'KYC_APPROVED' },
    { label: 'KYC_REJECTED', value: 'KYC_REJECTED' }
];

const KycProcessing = () => {
    const auth = useAuth();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [data, setData] = useState<KycProcessingData[]>([]);
    const [searchType, setSearchType] = useState('vin');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [fetchData, { data: queryData, loading: queryLoading }] = useLazyQuery(
        GET_KYC_PROCESSING_DATA,
        { fetchPolicy: 'network-only', notifyOnNetworkStatusChange: true }
    );

    const totalCount = queryData?.getKycProcessingData?.count ?? 0;
    const [exportLoading, setExportLoading] = useState(false);
    useEffect(() => {
        if (queryData?.getKycProcessingData?.data) {
            setData(queryData.getKycProcessingData.data || []);
        }
    }, [queryData]);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchWithDebounce = useCallback(
        (overrides?: { searchTerm?: string; searchType?: string; statusFilter?: string; rowsPerPage?: number }) => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                const term = overrides?.searchTerm ?? searchTerm;
                const type = overrides?.searchType ?? searchType;
                const status = overrides?.statusFilter ?? statusFilter;
                const rows = overrides?.rowsPerPage ?? rowsPerPage;
                fetchData({
                    variables: {
                        offset: 0,
                        limit: rows,
                        searchTerm: term,
                        searchType: type,
                        status: status === 'ALL' ? null : status,
                    },
                });
            }, 500);
        },
        [searchTerm, searchType, statusFilter, rowsPerPage, fetchData]
    );

    useEffect(() => {
        fetchData({
            variables: {
                offset: 0,
                limit: rowsPerPage,
                searchTerm,
                searchType,
                status: statusFilter === 'ALL' ? null : statusFilter,
            },
        });
    }, []);

    const client = useApolloClient();

    const handleExportToExcel = async () => {
        try {
            setExportLoading(true);

            let allData: KycProcessingData[] = [];
            let offset = 0;
            const limit = 500;

            while (true) {
                const { data: res } = await client.query({
                    query: GET_KYC_PROCESSING_DATA,
                    variables: {
                        offset,
                        limit,
                        searchTerm,
                        searchType,
                        status: statusFilter === 'ALL' ? null : statusFilter
                    },
                    fetchPolicy: 'network-only'
                });

                const chunk = res?.getKycProcessingData?.data || [];

                if (chunk.length === 0) break;

                allData.push(...chunk);

                if (chunk.length < limit) break;

                offset += limit;
            }

            const formattedData = allData.map((item, index) => ({
                'Sr.No': index + 1,
                'VIN': item.vin || "N/A",
                'IMEI': item.imei || "N/A",
                'Engine Number': item.engine_number || "N/A",
                'Customer Name': item.customer_name || "N/A",
                'Customer Mobile': item.customer_mobile || "N/A",
                'Customer Email': item.customer_email || "N/A",
                'Customer Address': item.customer_address || "N/A",
                'Retail Invoice Number': item.retail_invoice_number || "N/A",
                'Dealer Code': item.dealer_code || "N/A",
                'Dealer Name': item.dealer_name || "N/A",
                'Dealer Address': item.dealer_address || "N/A",
                'Dealer Town': item.dealer_town || "N/A",
                'Dealer Contact Name': item.dealer_contact_name || "N/A",
                'Dealer Contact Number': item.dealer_contact_number || "N/A",
                'KYC Mode': item.kyc_mode || "N/A",
                'KYC Count': item.kyc_count || "0",
                'Vehicle Make': item.vehicle_make || "N/A",
                'Vehicle Model': item.vehicle_model || "N/A",
                'Vehicle Type': item.vehicle_type || "N/A",
                'Fuel Type': item.fuel_type || "N/A",
                'Year of Manufacture': item.year_of_manufacture || "N/A",
                'Project Name': item.project_name || "N/A",
                'Device Model': item.device_model || "N/A",
                'Device Serial Number': item.device_serial_number || "N/A",
                'Device IMEI Number': item.device_imei_number || "N/A",
                'ICCID Number': item.iccid_number || "N/A",
                'MSISDN 1': item.msisdn_1 || "N/A",
                'MSISDN 2': item.msisdn_2 || "N/A",
                'SIM Activation Status': item.sim_activation_status || "N/A",
                'Registration State': item.registration_state || "N/A",
                'RTO Name': item.rto_name || "N/A",
                'RTO Number': item.rto_number || "N/A",
                'Proof of Address': item.proof_of_address || "N/A",
                'Proof of Identity': item.proof_of_identity || "N/A",
                'Ticket Created': item.ticket_created || "N/A",
                'Request ID': item.request_id || "N/A",
                'Rejection Reason': item.rejection_reason || "N/A",
                'Status': item.status || "N/A",
                'Last KYC Received Date': formatEpochDate(item.last_kyc_recieved_date),
                'Last KYC Approved Date': formatEpochDate(item.kyc_approved_date),
                'KYC Rejected Date': formatEpochDate(item.kyc_rejected_date),
                'Common Layer Cert': item.comman_layer_cert || "N/A",
                'Common Layer Cert Issued Date': formatEpochDate(item.common_layer_cert_issued_date),
                'Fitment Cert': item.fitment_cert || "N/A",
                'Fitment Cert Issued Date': formatEpochDate(item.fitment_cert_issued_date),
            }));

            const ws = XLSX.utils.json_to_sheet(formattedData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "KYC Processing");

            XLSX.writeFile(wb, "KYC_Processing_List.xlsx");

        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setExportLoading(false);
        }
    };


    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        fetchData({
            variables: {
                offset: newPage * rowsPerPage,
                limit: rowsPerPage,
                searchTerm,
                searchType,
                status: statusFilter === 'ALL' ? null : statusFilter,
            },
        });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        fetchWithDebounce({ rowsPerPage: newRowsPerPage });
    };

    const handleSearchTypeChange = (newType: string) => {
        setSearchType(newType);
        setPage(0);
        fetchWithDebounce({ searchType: newType });
    };

    const formatEpochDate = (epoch: string | number | null | undefined) => {
        if (!epoch) return "N/A";

        const num = Number(epoch);

        const date = num < 1e12 ? new Date(num * 1000) : new Date(num);

        if (isNaN(date.getTime())) return "N/A";

        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    return (
        <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
            <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
                KYC Processing
            </Typography>
            <Card sx={{
                width: '100%',
                mx: 'auto',
                boxShadow: 2,
                borderRadius: 3,
                p: 0,
                display: 'flex',
                flexDirection: 'column',
                height: { xs: 'auto', md: 'calc(100vh - 180px)' },
                minHeight: 100,
                bgcolor: '#fff',
                flex: 1,
            }}>
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 20,
                    background: '#fff',
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    boxShadow: '0 2px 8px -4px #e0e0e0',
                    px: { xs: 1, sm: 2 },
                    pt: 2,
                    pb: 2,
                    left: 0,
                    right: 0,
                    width: '100%',
                    minWidth: 0,
                    overflow: 'visible',
                }}>
                    <Box
                        display="flex"
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
                    >
                        <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
                        >
                            <FormControl size="small" sx={{ minWidth: 120, background: '#f5f5f5', flex: '0 0 auto' }}>
                                <Select
                                    value={searchType}
                                    onChange={(e) => handleSearchTypeChange(e.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value="vin">Search by VIN</MenuItem>
                                    <MenuItem value="imei">Search by IMEI</MenuItem>
                                    <MenuItem value="dealer_code">Search by Dealer Code</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder={
                                    searchType === 'vin' ? 'Search by VIN' :
                                        searchType === 'imei' ? 'Search by IMEI' :
                                            'Search by Dealer Code'
                                }
                                value={searchTerm}
                                onChange={(e) => {
                                    const newTerm = e.target.value;
                                    setSearchTerm(newTerm);
                                    setPage(0);
                                    fetchWithDebounce({ searchTerm: newTerm });
                                }}
                                sx={{ background: "#f5f5f5", minWidth: 120, flex: 1 }}
                            />
                            {/* Filter Icon and Menu */}
                            <IconButton
                                sx={{ ml: 1, color: '#000000ff', flex: '0 0 auto' }}
                                onClick={e => setAnchorEl(e.currentTarget)}
                            >
                                <FilterListIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            >
                                {KYC_STATUS_OPTIONS.map(option => (
                                    <MenuItem
                                        key={option.value}
                                        selected={statusFilter === option.value}
                                        onClick={() => {
                                            setStatusFilter(option.value);
                                            setAnchorEl(null);
                                            setPage(0);
                                            fetchWithDebounce({ statusFilter: option.value });
                                        }}
                                    >
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                            {/* ...existing export button... */}
                            <Tooltip title="Export to Excel" placement="top" arrow>
                                <IconButton sx={{ ml: 1, color: '#000', flex: '0 0 auto' }} onClick={handleExportToExcel}>
                                    {exportLoading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, px: { xs: 0.5, sm: 2 }, py: 2, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <TableContainer
                            component={Paper}
                            sx={{
                                boxShadow: 'none',
                                borderRadius: 2,
                                width: '100%',
                                minWidth: 600,
                                background: '#fff',
                                overflow: 'visible',
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                sx={{
                                    minWidth: 600,
                                    width: '100%',
                                    tableLayout: 'auto',
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                }}
                            >
                                <TableHead>
                                    <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                                        <TableCell sx={{
                                            ...columnStyles.srNo, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Sr.No</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">VIN</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">IMEI</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Dealer Code</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Dealer Name</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">KYC Mode</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Vehicle Make</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Last KYC Received Date</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Last KYC Approved Date</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">KYC Count</TableCell>
                                        <TableCell sx={{
                                            ...cellStyle, backgroundColor: '#E60012', color: '#fff', position: 'sticky', top: 0, zIndex: 11, fontWeight: 'bold',
                                            borderRight: '1px solid #e0e0e0',
                                            boxShadow: '1px 0 0 0 rgba(224, 224, 224, 0.7) inset'
                                        }} align="center">Status</TableCell>

                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {queryLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                                                <CircularProgress size={24} />
                                            </TableCell>
                                        </TableRow>
                                    ) : data.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={12} align="center" sx={{ py: 4, color: '#888' }}>
                                                No KYC records found
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        data.map((item, idx) => (
                                            <TableRow
                                                key={`${item.vin}-${idx}`}
                                                hover
                                                sx={{
                                                    backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9',
                                                    '&:hover': { backgroundColor: '#f0f0f0' }
                                                }}
                                            >
                                                <TableCell align="center" sx={columnStyles.srNo}>
                                                    {page * rowsPerPage + idx + 1}
                                                </TableCell>
                                                <TableCell align="center" sx={cellStyle}>
                                                    <Tooltip title={item.vin} placement="top" arrow>
                                                        <span>{item.vin || "N/A"}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="center" sx={cellStyle}>
                                                    <Tooltip title={item.imei} placement="top" arrow>
                                                        <span>{item.imei || "N/A"}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="center" sx={cellStyle}>{item.dealer_code || "N/A"}</TableCell>
                                                <TableCell align="center" sx={cellStyle}>{item.dealer_name || "N/A"}</TableCell>
                                                <TableCell align="center" sx={cellStyle}>{item.kyc_mode || "N/A"}</TableCell>
                                                <TableCell align="center" sx={cellStyle}>{item.vehicle_make || "N/A"}</TableCell>
                                                <TableCell align="center" sx={cellStyle}>
                                                    {formatEpochDate(item.last_kyc_recieved_date)}
                                                </TableCell>

                                                <TableCell align="center" sx={cellStyle}>
                                                    {formatEpochDate(item.kyc_approved_date)}
                                                </TableCell>
                                                <TableCell align="center" sx={cellStyle}>{item.kyc_count || "0"}</TableCell>
                                                <TableCell align="center" sx={cellStyle}>
                                                    {item.status || "N/A"}

                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </Box>

                <Box sx={{
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 10,
                    background: '#fff',
                    borderTop: '1px solid #e0e0e0',
                    py: 1,
                    px: 2
                }}>
                    <TablePagination
                        rowsPerPageOptions={[20, 50, 100, 500]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Rows per page:"
                        sx={{
                            '.MuiTablePagination-toolbar': {
                                px: 0,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                '@media (min-width: 600px)': {
                                    flexWrap: 'nowrap',
                                    justifyContent: 'space-between'
                                }
                            }
                        }}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default KycProcessing;